export interface SubscriptionPlan {
  id: string;
  title: string;
  description: string;
  price: number;
  annualPrice?: number;
  currency: string;
  interval: string;
  features: string[];
  isPopular?: boolean;
  isAvailable: boolean;
  isBeta?: boolean;
  category: 'corporate' | 'law-enforcement';
  maxSites?: number;
  maxReports?: number;
  historyRetention?: string;
  maxUsers?: number;
  hasGeoLocation?: boolean;
}

export const Subscriptions: SubscriptionPlan[] = [
  // Corporate Plans
  {
    id: 'Corp-Basic',
    title: 'Basic Plan',
    description: 'Perfect for getting started with SkimBuster',
    price: 20,
    annualPrice: 200,
    currency: 'USD',
    interval: 'month',
    category: 'corporate',
    maxSites: 2,
    maxReports: 50,
    historyRetention: '12 months',
    maxUsers: 5,
    features: [
      'Up to 2 sites monitoring',
      '12 months history retention',
      'Up to 5 users',
      'Basic skimming detection',
      'Email notifications',
      'Community support'
    ],
    isPopular: true,
    isAvailable: true,
    isBeta: true
  },
  {
    id: 'Corp-Premium',
    title: 'Premium Plan',
    description: 'Advanced features for growing businesses',
    price: 30,
    annualPrice: 300,
    currency: 'USD',
    interval: 'month',
    category: 'corporate',
    maxSites: 5,
    maxReports: 1000,
    historyRetention: '12 months',
    maxUsers: 20,
    features: [
      'Up to 5 sites monitoring',
      '12 months history retention',
      'Up to 20 users',
      'Scan analytics',
      'Priority support'
    ],
    isPopular: false,
    isAvailable: true,
    isBeta: false
  },
  {
    id: 'Corp-Ultra',
    title: 'Ultra Plan',
    description: 'Complete solution for larger organizations',
    price: 50,
    annualPrice: 500,
    currency: 'USD',
    interval: 'month',
    category: 'corporate',
    maxSites: 15,
    historyRetention: '12 months',
    maxUsers: 50,
    features: [
      'Up to 15 sites monitoring',
      '12 months history retention',
      'Up to 50 users',
      'Scan analytics',
      'Advanced threat detection',
      'Priority support'
    ],
    isPopular: false,
    isAvailable: true,
    isBeta: false
  },
  {
    id: 'Corp-Enterprise',
    title: 'Enterprise Plan',
    description: 'Custom solution for enterprise organizations',
    price: 0, // Contact for pricing
    currency: 'USD',
    interval: 'month',
    category: 'corporate',
    features: [
      'Custom site monitoring scope',
      'Custom history retention',
      'Custom user seating',
      'Full API access',
      'Custom integrations',
      'Dedicated support',
      'White-label options',
      'All product options available per your agreement',
    ],
    isPopular: false,
    isAvailable: true,
    isBeta: false
  },
  // Law Enforcement Plans
  {
    id: 'LEO-Basic',
    title: 'Basic Plan',
    description: 'Essential tools for law enforcement agencies',
    price: 20,
    annualPrice: 200,
    currency: 'USD',
    interval: 'month',
    category: 'law-enforcement',
    maxSites: 0, // No site monitoring
    historyRetention: '12 months',
    maxUsers: 20,
    hasGeoLocation: true,
    features: [
      'Geo-location on scans',
      '12 months history retention',
      'Up to 20 users',
      'Scan analytics',
      'Advanced threat detection',
      'Priority support'
    ],
    isPopular: false,
    isAvailable: true,
    isBeta: false
  },
  {
    id: 'LEO-Ultra',
    title: 'Ultra Plan',
    description: 'Advanced capabilities for law enforcement operations',
    price: 50,
    annualPrice: 500,
    currency: 'USD',
    interval: 'month',
    category: 'law-enforcement',
    maxSites: 0, // No site monitoring
    historyRetention: '12 months',
    maxUsers: 50,
    hasGeoLocation: true,
    features: [
      'Geo-location on scans',
      '12 months history retention',
      'Up to 50 users',
      'Scan analytics',
      'Advanced threat detection',
      'Priority support'
    ],
    isPopular: false,
    isAvailable: true,
    isBeta: false
  },
  {
    id: 'LEO-Enterprise',
    title: 'Enterprise Plan',
    description: 'Custom solution for large law enforcement agencies',
    price: 0, // Contact for pricing
    currency: 'USD',
    interval: 'month',
    category: 'law-enforcement',
    maxSites: 0, // No site monitoring
    hasGeoLocation: true,
    features: [
      'Geo-location on scans',
      'Custom history retention',
      'Custom user seating',
      'Full API access',
      'Custom integrations',
      'Dedicated support',
      'Advanced analytics',
      'All product options available per your agreement',
    ],
    isPopular: false,
    isAvailable: true,
    isBeta: false
  }
];

export const getAvailablePlans = (): SubscriptionPlan[] => {
  return Subscriptions.filter(plan => plan.isAvailable);
};

export const getBetaPlans = (): SubscriptionPlan[] => {
  return Subscriptions.filter(plan => plan.isBeta);
};

export const getPlanById = (id: string): SubscriptionPlan | undefined => {
  return Subscriptions.find(plan => plan.id === id);
};

export const getPlansByCategory = (category: 'corporate' | 'law-enforcement'): SubscriptionPlan[] => {
  return Subscriptions.filter(plan => plan.category === category && plan.isAvailable);
};

export const getCorporatePlans = (): SubscriptionPlan[] => {
  return getPlansByCategory('corporate');
};

export const getLawEnforcementPlans = (): SubscriptionPlan[] => {
  return getPlansByCategory('law-enforcement');
};

export const getPopularPlan = (category?: 'corporate' | 'law-enforcement'): SubscriptionPlan | undefined => {
  const plans = category ? getPlansByCategory(category) : getAvailablePlans();
  return plans.find(plan => plan.isPopular);
};

export const formatPrice = (plan: SubscriptionPlan, isAnnual: boolean = false): string => {
  // Safety checks to handle edge cases
  if (!plan) {
    return 'N/A';
  }
  
  if (!plan.id || typeof plan.id !== 'string') {
    return plan.price === 0 ? 'Free' : `$${plan.price}/${plan.interval || 'month'}`;
  }
  
  if (plan.price === 0) {
    return plan.id.toLowerCase().includes('enterprise') ? 'Contact for pricing' : 'Free';
  }
  
  const price = isAnnual && plan.annualPrice ? plan.annualPrice : plan.price;
  const interval = isAnnual ? 'year' : plan.interval;
  
  return `$${price}/${interval}`;
};

export const getAnnualSavings = (plan: SubscriptionPlan): number => {
  if (!plan.annualPrice || plan.price === 0) return 0;
  const monthlyTotal = plan.price * 12;
  return monthlyTotal - plan.annualPrice;
};